import Card, { CardProps } from "../components/Card";
import Hero from "../components/Hero";
import Layout from "../components/Layout";
import React from "react";
import {
  Box,
  Container,
  Divider,
  Heading,
  Link,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { GatsbySeo } from "gatsby-plugin-next-seo";
import { HeadFC } from "gatsby";
import { ReactComponent as TriCube } from "../images/tri-cube.svg";

const TutorialCard = ({ title, description, url, image }: CardProps) => {
  return (
    <Card
      title={title}
      description={description}
      url={url}
      image={image}
      onClickEventName="tutorial_card_click"
    />
  );
};

type RowProps = {
  first?: boolean;
  title: string;
  description: string;
  children: React.ReactElement<CardProps> | React.ReactElement<CardProps>[];
};

type HeaderDescriptionProps = {
  title: string;
  description: string;
};

const TutorialRow = ({ first, title, description, children }: RowProps) => {
  return (
    <>
      <Divider
        mt={first ? "0" : "56px"}
        mb={first ? "64px" : "56px"}
        color="silver.500"
      />
      <SimpleGrid spacing={4} columns={{ base: 1, lg: 2 }}>
        <RowHeaderDescription title={title} description={description} />
        <SimpleGrid spacing="4" columns={1}>
          {children}
        </SimpleGrid>
      </SimpleGrid>
    </>
  );
};

const RowHeaderDescription = ({
  title,
  description,
}: HeaderDescriptionProps) => (
  <Box>
    <Heading as="h2" variant="heading2" mb="2" color="gray.700">
      {title}
    </Heading>
    <Text maxW={{ base: "100%", lg: "80%" }} variant="bodySm">
      {description}
    </Text>
  </Box>
);

const CertificationRow = ({
  first,
  title,
  description,
  children,
}: RowProps) => {
  return (
    <>
      <Divider
        mt={first ? "0" : "56px"}
        mb={first ? "64px" : "56px"}
        color="silver.500"
      />
      <SimpleGrid spacing={4} columns={1}>
        <RowHeaderDescription title={title} description={description} />
        <SimpleGrid spacing="12" columns={{ base: 1, lg: 2 }}>
          {children}
        </SimpleGrid>
      </SimpleGrid>
    </>
  );
};

export default function TutorialsPage() {
  return (
    <>
      <GatsbySeo
        title="GraphQL Tutorials"
        description="Learn the fundamentals of schemas and queries, then implement some apps with hands-on step-by-step tutorials."
      />
      <Layout>
        <Container maxW="7xl" p={10}>
          <Hero title="GraphQL Tutorials" image={<TriCube />}>
            <Text>
              Learn the fundamentals of schemas and queries, then implement some
              apps with hands-on step-by-step tutorials with &nbsp;
              <Link
                color="purple.500"
                _hover={{
                  color: "purple.700",
                }}
                target="_blank"
                href="https://apollographql.com/tutorials?utm_campaign=graphql.com"
              >
                Apollo&rsquo;s Odyssey platform.
              </Link>
            </Text>
          </Hero>
          <TutorialRow
            first
            title="GraphQL Basics"
            description="Leverage instructor-led tutorials specifically designed for GraphQL beginners."
          >
            <TutorialCard
              title="Lift-off Series"
              image="https://res.cloudinary.com/apollographql/image/upload/v1694618911/www/graphqldotcom/lift-off-series.png"
              description="Learn how to design a schema, run an Apollo Server 4, and perform queries with Apollo Client 3 on the frontend."
              url="https://www.apollographql.com/tutorials/lift-off-part1?utm_campaign=graphql.com"
            />
          </TutorialRow>
          <TutorialRow
            title=" Getting Started with Clients"
            description="Get familiar and comfortable with executing GraphQL operations,
            caching, and managing all of your application's state in one
            place."
          >
            <TutorialCard
              title="React: Get Started"
              description="Get up and running with Apollo Client in React"
              url="https://www.apollographql.com/docs/react/get-started?utm_campaign=graphql.com"
            />
            <TutorialCard
              title="Kotlin: Get Started"
              description="Get up and running with GraphQL on Android with Apollo Kotlin"
              url="https://www.apollographql.com/docs/kotlin/#getting-started?utm_campaign=graphql.com"
            />
            <TutorialCard
              title="iOS: Get Started"
              description="Get up and running with GraphQL on iOS with Apollo iOS"
              url="https://www.apollographql.com/docs/ios/get-started?utm_campaign=graphql.com"
            />
          </TutorialRow>
          <TutorialRow
            title="GraphOS Basics"
            description="Level up your GraphQL APIs with Apollo GraphOS, the platform for running production-ready graphs at scale."
          >
            <TutorialCard
              title="GraphOS"
              image="https://res.cloudinary.com/apollographql/image/upload/v1697474689/www/graphqldotcom/michelle-graphos.png"
              description="Learn how GraphOS can take your GraphQL API from development to launch with tooling, metrics, and performance benefits."
              url="https://www.apollographql.com/tutorials/getting-started-with-graphos?utm_campaign=graphql.com"
            />
          </TutorialRow>
          <CertificationRow
            title="GraphQL Certifications"
            description="Choose from guided tutorials or exams, and become a certified GraphQL developer with Apollo Odyssey for free."
          >
            <TutorialCard
              title="Apollo Graph Associate"
              image="https://res.cloudinary.com/apollographql/image/upload/v1694717542/www/graphqldotcom/associate-sm_erhqti.png"
              description="Level up your dev skills with the basics of GraphQL and Apollo, and earn your certification as an Apollo Graph Associate."
              url="https://www.apollographql.com/tutorials/certifications/apollo-graph-associate?utm_campaign=graphql.com"
            />
            <TutorialCard
              title="Apollo Graph Professional"
              image="https://res.cloudinary.com/apollographql/image/upload/v1694717555/www/graphqldotcom/professional-sm_fgqcjc.png"
              description="Take your GraphQL expertise one step further—dive into Apollo Federation, the industry-standard for implementing graphs at scale, and earn your Apollo Graph Professional certification."
              url="https://www.apollographql.com/tutorials/certifications/apollo-graph-professional?utm_campaign=graphql.com"
            />
          </CertificationRow>
        </Container>
      </Layout>
    </>
  );
}

export const Head: HeadFC = () => <title>Tutorials - GraphQL.com</title>;
